import { formatMoney, purchaseRhythm, type Currency, type ItemHistory } from '@sp/shared'
import { everyText, formatDay, relativeAgo } from '../lib/format.ts'
import { Label, Paragraph } from './ui.tsx'

/** Kolik poslednich cen se vypise pod grafem. */
const SHOWN_PRICES = 5

/** Jak casto se polozka kupuje a jak se menila cena — z dokoncenych nakupu. */
export function PriceHistory({ history }: { history: ItemHistory }) {
  const rhythm = purchaseRhythm(history.purchases.map((p) => p.date))
  const priced = history.purchases.flatMap((p) =>
    p.priceMinor !== null && p.currency !== null ? [{ date: p.date, minor: p.priceMinor, currency: p.currency }] : [],
  )
  // Ceny v zlotych a eurech do jedne cary nepatri — bere se nejcastejsi mena.
  const counts = new Map<Currency, number>()
  for (const p of priced) counts.set(p.currency, (counts.get(p.currency) ?? 0) + 1)
  const currency = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null
  const points = priced.filter((p) => p.currency === currency).reverse()
  const last = history.purchases[0]
  if (!last || (!rhythm && points.length === 0)) return null

  return (
    <div className="price-history">
      <Label as="span">Z nákupů</Label>
      <Paragraph size="meta" tone="fg">
        {rhythm
          ? `Kupujete ${everyText(rhythm.intervalDays)}, naposledy ${relativeAgo(last.date)}.`
          : `Naposledy koupeno ${relativeAgo(last.date)}.`}
      </Paragraph>
      {points.length >= 2 && currency && (
        <Sparkline values={points.map((p) => p.minor)} label={`Vývoj ceny v ${currency}`} />
      )}
      {currency && points.length > 0 && (
        <div className="stack-4">
          {points
            .slice(-SHOWN_PRICES)
            .reverse()
            .map((p, i) => (
              <div key={`${p.date}-${i}`} className="money-line">
                <span className="meta">{formatDay(p.date)}</span>
                <span className="money-line-value is-strong">{formatMoney(p.minor, currency)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

function Sparkline({ values, label }: { values: readonly number[]; label: string }) {
  const width = 240
  const height = 56
  const pad = 6
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const x = (i: number) => pad + (i * (width - 2 * pad)) / Math.max(1, values.length - 1)
  // Stejne ceny kresli rovnou caru uprostred, ne na spodnim okraji.
  const y = (v: number) => (max === min ? height / 2 : height - pad - ((v - min) * (height - 2 * pad)) / span)
  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const lastIndex = values.length - 1
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={label}>
      <polygon className="sparkline-area" points={`${x(0).toFixed(1)},${height - pad} ${line} ${x(lastIndex).toFixed(1)},${height - pad}`} />
      <polyline className="sparkline-line" points={line} />
      <circle className="sparkline-dot" cx={x(lastIndex)} cy={y(values[lastIndex] ?? min)} r="3.5" />
    </svg>
  )
}
